import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  Bell,
  CalendarDays,
  LayoutDashboard,
  ShoppingBag,
  Store,
  UtensilsCrossed,
  UserCog,
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useNotificationsStore } from '@/features/notifications';
import type { SidebarMode } from './AppSidebar';

interface BottomTab {
  id: string;
  label: string;
  icon: React.ElementType;
  href: string;
  exact?: boolean;
}

function buildRestaurantTabs(restaurantId: string): BottomTab[] {
  const base = `/app/restaurants/${restaurantId}`;
  return [
    { id: 'orders', label: 'nav.orders', icon: ShoppingBag, href: `${base}/orders` },
    { id: 'dashboard', label: 'nav.dashboard', icon: LayoutDashboard, href: base, exact: true },
    { id: 'menu', label: 'nav.menu', icon: UtensilsCrossed, href: `${base}/menu` },
    { id: 'reservations', label: 'nav.reservations', icon: CalendarDays, href: `${base}/reservations` },
    { id: 'analytics', label: 'nav.analytics', icon: BarChart3, href: `${base}/analytics` },
  ];
}

const accountTabs: BottomTab[] = [
  { id: 'restaurants', label: 'nav.restaurants', icon: Store, href: '/app', exact: true },
  { id: 'notifications', label: 'nav.notifications', icon: Bell, href: '/app/notifications' },
  { id: 'settings', label: 'nav.settings', icon: UserCog, href: '/app/settings' },
];

interface MobileBottomNavProps {
  mode: SidebarMode;
  restaurantId?: string;
}

export function MobileBottomNav({ mode, restaurantId }: MobileBottomNavProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const unreadCount = useNotificationsStore((s) => s.unreadCount);

  const tabs =
    mode === 'restaurant' && restaurantId
      ? buildRestaurantTabs(restaurantId)
      : accountTabs;

  function isActive(tab: BottomTab): boolean {
    if (tab.exact) return location.pathname === tab.href || location.pathname === `${tab.href}/`;
    return location.pathname === tab.href || location.pathname.startsWith(`${tab.href}/`);
  }

  return (
    <nav
      className="shrink-0 border-t border-border bg-card/90 backdrop-blur-sm"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      <div className="flex items-stretch h-[64px]">
        {tabs.map((tab) => {
          const active = isActive(tab);
          const Icon = tab.icon;
          const showBadge = tab.id === 'notifications' && unreadCount > 0;

          return (
            <NavLink
              key={tab.id}
              to={tab.href}
              className="flex-1 flex flex-col items-center justify-center gap-1 relative min-h-[44px]"
              aria-label={t(tab.label, { defaultValue: tab.label })}
            >
              {/* Active pill background */}
              {active && (
                <span className="absolute inset-x-2 top-1.5 h-8 rounded-xl bg-accent/12" />
              )}
              <div className="relative z-10">
                <Icon
                  className={cn(
                    'h-[22px] w-[22px] transition-colors',
                    active ? 'text-accent' : 'text-muted-foreground',
                  )}
                  strokeWidth={active ? 2.5 : 1.75}
                />
                {showBadge && (
                  <span className="absolute -top-1 -right-1.5 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
              <span
                className={cn(
                  'text-[10px] font-medium transition-colors leading-none z-10',
                  active ? 'text-accent font-semibold' : 'text-muted-foreground',
                )}
              >
                {t(tab.label, { defaultValue: tab.id })}
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
